import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import DateInput from '../common/DateInput';
import { tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj } from '../common/TableSearch';
import { akcizeApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { datum, isoDatum, lit, din, mesecNaziv } from './format';

const PRAZNA = { datum: null, brojRacuna: '', kolicina: null, dobavljac: '', iznos: null, brojIzvoda: '', napomena: '' };

/** nabavke goriva u kvartalu: računi, količina, dobavljač, iznos, izvod (dokaz plaćanja) */
export default function Nabavke({ data, companyId, onIzmena }) {
    const alerts = useAlerts();
    const [forma, setForma] = useState(null);
    const [radi, setRadi] = useState(false);
    const [pretraga, setPretraga] = useState('');
    const nabavke = useMemo(() => filtriraj(data.nabavke, pretraga, (n) => [datum(n.datum), n.brojRacuna, lit(n.kolicina), n.dobavljac, din(n.iznos),
        n.brojIzvoda || 'nema', n.izvor === 'excel' ? 'uvoz' : 'ručno']), [data.nabavke, pretraga]);
    const set = (p) => setForma((f) => ({ ...f, ...p }));

    const otvori = (n) => setForma(n ? { ...n, datum: n.datum ? new Date(`${isoDatum(n.datum)}T00:00:00`) : null } : { ...PRAZNA });
    const sacuvaj = async () => {
        if (!forma.datum || !forma.brojRacuna || !forma.kolicina) { alerts.warn('Datum, broj računa i količina su obavezni.', 'Nabavka'); return; }
        setRadi(true);
        try {
            await akcizeApi.sacuvajNabavku({ ...forma, datum: isoDatum(forma.datum), companyId });
            alerts.success('Nabavka je sačuvana.');
            setForma(null);
            onIzmena();
        } catch { /* toast */ }
        setRadi(false);
    };
    const obrisi = async (n) => {
        if (!(await alerts.confirm('Brisanje', `Obrisati račun ${n.brojRacuna} od ${datum(n.datum)}?`))) return;
        try { await akcizeApi.obrisiNabavku(n.id); onIzmena(); } catch { /* toast */ }
    };

    return (
        <>
            <div className="akc-zbir">
                {data.zbir.poMesecu.map((m) => (
                    <div key={m.mesec} className="akc-zbir-kartica">
                        <span className="muted">{mesecNaziv(m.mesec)}</span>
                        <b>{lit(m.nabavljeno)} L</b>
                        <small className={m.izdato > m.nabavljeno ? 'akc-iznad' : 'muted'}>izdato vozilima {lit(m.izdato)} L</small>
                    </div>
                ))}
                <div className="akc-zbir-kartica ukupno">
                    <span className="muted">Kvartal</span>
                    <b>{lit(data.zbir.nabavljeno)} L</b>
                    <small className="muted">izdato {lit(data.zbir.izdato)} L</small>
                </div>
                <Button label="Nova nabavka" icon="pi pi-plus" onClick={() => otvori(null)} className="akc-dodaj" />
            </div>
            <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (datum, račun, dobavljač, izvod, iznos...)" label="Pretraga nabavki"
                ukupno={data.nabavke.length} pronadjeno={nabavke.length} jedinica="nabavki" />
            <DataTable value={nabavke} dataKey="id" size="small" stripedRows emptyMessage={tableEmpty(pretraga.trim() ? 'Nijedna nabavka ne odgovara pretrazi.' : 'Nema nabavki u ovom kvartalu.')}>
                <Column header="Datum" body={(n) => datum(n.datum)} />
                <Column field="brojRacuna" header="Broj računa" />
                <Column header="Količina (L)" body={(n) => lit(n.kolicina)} className="num" />
                <Column field="dobavljac" header="Dobavljač" />
                <Column header="Iznos (din)" body={(n) => din(n.iznos)} className="num" />
                <Column header="Broj izvoda" body={(n) => n.brojIzvoda || <span className="akc-iznad" title="Nema dokaza plaćanja">nema</span>} />
                <Column header="Izvor" body={(n) => (n.izvor === 'excel' ? 'uvoz' : 'ručno')} />
                <Column body={(n) => (
                    <div className="row-actions">
                        <Button icon="pi pi-pencil" rounded text aria-label="Izmeni" tooltip="Izmeni" onClick={() => otvori(n)} />
                        <Button icon="pi pi-trash" rounded text severity="danger" aria-label="Obriši" tooltip="Obriši" onClick={() => obrisi(n)} />
                    </div>
                )} style={{ width: '6rem' }} />
            </DataTable>
            {forma && (
                <Dialog header={forma.id ? 'Izmena nabavke' : 'Nova nabavka'} visible onHide={() => setForma(null)} style={{ width: 'min(36rem, 94vw)' }} modal draggable={false}
                    footer={<div className="dialog-footer"><Button label="Otkaži" text severity="secondary" onClick={() => setForma(null)} /><Button label="Sačuvaj" icon="pi pi-check" loading={radi} onClick={sacuvaj} /></div>}>
                    <div className="form-grid">
                        <div className="field"><label htmlFor="nab-dat">Datum <span className="req">*</span></label><DateInput inputId="nab-dat" value={forma.datum} onChange={(e) => set({ datum: e.value })} showIcon /></div>
                        <div className="field"><label htmlFor="nab-br">Broj računa <span className="req">*</span></label><InputText id="nab-br" value={forma.brojRacuna} maxLength={60} onChange={(e) => set({ brojRacuna: e.target.value })} /></div>
                        <div className="field"><label htmlFor="nab-kol">Količina (L) <span className="req">*</span></label><InputNumber inputId="nab-kol" value={forma.kolicina} onValueChange={(e) => set({ kolicina: e.value })} min={0} maxFractionDigits={2} locale="sr-RS" /></div>
                        <div className="field"><label htmlFor="nab-dob">Dobavljač</label><InputText id="nab-dob" value={forma.dobavljac || ''} maxLength={120} onChange={(e) => set({ dobavljac: e.target.value })} /></div>
                        <div className="field"><label htmlFor="nab-izn">Iznos (din)</label><InputNumber inputId="nab-izn" value={forma.iznos} onValueChange={(e) => set({ iznos: e.value })} min={0} minFractionDigits={2} maxFractionDigits={2} locale="sr-RS" /></div>
                        <div className="field"><label htmlFor="nab-izv">Broj izvoda (plaćanje)</label><InputText id="nab-izv" value={forma.brojIzvoda || ''} maxLength={120} onChange={(e) => set({ brojIzvoda: e.target.value })} /></div>
                        <div className="field" style={{ gridColumn: '1 / -1' }}><label htmlFor="nab-nap">Napomena</label><InputText id="nab-nap" value={forma.napomena || ''} maxLength={400} onChange={(e) => set({ napomena: e.target.value })} /></div>
                    </div>
                </Dialog>
            )}
        </>
    );
}
